import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import Divider from '@mui/joy/Divider';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import CircularProgress from '@mui/joy/CircularProgress';
import Sheet from '@mui/joy/Sheet';
import Check from '@mui/icons-material/Check';
import Star from '@mui/icons-material/Star';
// import Bolt from '@mui/icons-material/Bolt';
import { GET_TIERS, GET_MY_SUBSCRIPTION, GET_CLIENT } from '../../gql/subscription.queries';
import { CREATE_CHECKOUT_SESSION, CHANGE_TIER } from '../../gql/subscription.mutations';
import { useFeedback } from '../feedback/Service';

export default function PricingPage() {
  const navigate = useNavigate();
  const feedback = useFeedback();

  const { data: tiersData, loading: tiersLoading } = useQuery(GET_TIERS);
  const { data: subscriptionData, loading: subscriptionLoading, refetch: refetchSubscription } = useQuery(GET_MY_SUBSCRIPTION, { fetchPolicy: 'network-only' });
  const { data: clientData, loading: clientLoading, refetch: refetchClient } = useQuery(GET_CLIENT, { fetchPolicy: 'network-only' });

  const [createCheckoutSession, { loading: checkoutLoading }] = useMutation(CREATE_CHECKOUT_SESSION);
  const [changeTier, { loading: changeTierLoading }] = useMutation(CHANGE_TIER);

  const currentSubscription = subscriptionData?.mySubscription;
  const client = clientData?.getClient;
  const currentTierId = currentSubscription?.tier?._id || client?.currentTier?._id;
  const tiers = (tiersData?.tiers || []).filter(tier => currentTierId ? !tier.isFree : true); // Hide free tier if user already has a subscription

  const isLoading = tiersLoading || subscriptionLoading || clientLoading;
  const isMutating = checkoutLoading || changeTierLoading;

  const handleSelectTier = async (tier) => {
    try {
      if (!currentTierId) {
        // New user - create checkout session
        const { data } = await createCheckoutSession({
          variables: { tierId: tier._id }
        });
        if (data?.createCheckoutSession?.url) {
          window.location.href = data.createCheckoutSession.url;
        } else {
          await Promise.all([refetchSubscription(), refetchClient()]);
          navigate('/jobs');
        }
      } else {
        // Existing user - change tier
        const { data } = await changeTier({
          variables: { tierId: tier._id }
        });
        if (data?.changeTier?.url) {
          // Requires payment - redirect to checkout
          window.location.href = data.changeTier.url;
        } else {
          // Tier changed instantly
          await Promise.all([refetchSubscription(), refetchClient()]);
          navigate('/settings/billing');
        }
      }
    } catch (error) {
      feedback.snackbar({ text: error.message || 'Failed to process subscription', type: "error" });
    }
  };

  const getButtonText = (tier) => {
    if (tier._id === currentTierId) return 'Current Plan';
    if (!currentTierId) return tier.isFree ? 'Start Free' : 'Get Started';

    const currentTier = tiers.find(t => t._id === currentTierId);
    if (!currentTier) return 'Get Started';

    if (tier.monthlyFee > currentTier.monthlyFee) return 'Upgrade';
    return 'Downgrade';
  };

  const isCurrentPlan = (tier) => tier._id === currentTierId;

  // Determine which tier is "popular" (usually second or middle tier)
  const getPopularIndex = () => {
    if (tiers.length <= 2) return tiers.length - 1;
    return Math.floor(tiers.length / 2);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      py: { xs: 3, md: 5 },
      px: 2,
      maxWidth: 1400,
      mx: 'auto',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
        {/* <Chip
          size="sm"
          variant="soft"
          color="primary"
          startDecorator={<Bolt />}
          sx={{ mb: 2 }}
        >
          Simple, transparent pricing
        </Chip> */}
        <Typography
          level="h1"
          sx={{
            mb: 1.5,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            fontWeight: 700
          }}
        >
          Choose the right plan for your team
        </Typography>
        <Typography
          level="body-lg"
          color="neutral"
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          Start hiring smarter with AI-powered video interviews.
          All plans include unlimited team members.
        </Typography>
        <Typography level="body-sm" color="neutral" sx={{ mt: 1, mb: 0, p: 0 }}>
          1 credit = 1 completed video interview. Unused credits roll over monthly.
        </Typography>
      </Box>

      {/* Pricing Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: `repeat(${Math.min(tiers.length, 4)}, 1fr)`
          },
          gap: { xs: 2, md: 3 },
          alignItems: 'stretch',
          maxWidth: tiers.length <= 3 ? 1000 : 1400,
          mx: 'auto'
        }}
      >
        {tiers.map((tier, index) => {
          const isPopular = index === getPopularIndex();
          const isCurrent = isCurrentPlan(tier);

          return (
            <Card
              key={tier._id}
              variant="outlined"
              sx={{
                position: 'relative',
                borderColor: isPopular ? 'primary.400' : 'neutral.200',
                borderWidth: isPopular ? 2 : 1,
                borderRadius: 'xl',
                overflow: 'visible',
                transition: 'all 0.2s ease-in-out',
                bgcolor: 'background.surface',
                '&:hover': {
                  borderColor: isPopular ? 'primary.500' : 'neutral.400',
                  boxShadow: 'lg'
                }
              }}
            >
              {/* Popular Badge */}
              {isPopular && (
                <Sheet
                  variant="solid"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: -14,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 'xl',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <Star sx={{ fontSize: 14 }} />
                  <Typography level="body-xs" sx={{ color: 'inherit', fontWeight: 600 }}>
                    MOST POPULAR
                  </Typography>
                </Sheet>
              )}

              {/* Current Plan Badge */}
              {isCurrent && (
                <Chip
                  size="sm"
                  variant="soft"
                  color="success"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12
                  }}
                >
                  Current
                </Chip>
              )}

              <CardContent sx={{ p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Tier Name */}
                <Typography
                  level="title-lg"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  {tier.name}
                </Typography>

                {tier.description && (
                  <Typography
                    level="body-sm"
                    color="neutral"
                    sx={{ mb: 2, minHeight: 40 }}
                  >
                    {tier.description}
                  </Typography>
                )}

                {/* Price */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography
                      level="h2"
                      sx={{ fontSize: '2.5rem', fontWeight: 700, lineHeight: 1 }}
                    >
                      {tier.isFree ? '$0' : `$${tier.monthlyFee}`}
                    </Typography>
                    <Typography level="body-md" color="neutral">
                      /month
                    </Typography>
                  </Box>
                  {!tier.isFree && tier.monthlyFee > 0 && (
                    <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                      Billed monthly
                    </Typography>
                  )}
                </Box>

                {/* Credits Highlight */}
                <Sheet
                  variant="soft"
                  color={isPopular ? 'primary' : 'neutral'}
                  sx={{
                    p: 1.5,
                    borderRadius: 'md',
                    mb: 2,
                    textAlign: 'center'
                  }}
                >
                  <Typography level="title-md" sx={{ fontWeight: 600 }}>
                    {tier.monthlyCredits} credits
                  </Typography>
                  <Typography level="body-xs" color="neutral">
                    per month
                  </Typography>
                </Sheet>

                {/* Additional Info */}
                <Box sx={{ mb: 2 }}>
                  <Typography level="body-xs" color="neutral">
                    Max {tier.maxCreditsAllowed} credits
                  </Typography>
                  {!tier.isFree && !!tier.pricePerAdditionalCredit && (
                    <Typography level="body-xs" color="neutral">
                      +${tier.pricePerAdditionalCredit}/extra credit
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Features */}
                <List size="sm" sx={{ gap: 1, flex: 1, '--ListItem-paddingY': '2px' }}>
                  {tier.features?.map((feature, idx) => (
                    <ListItem key={idx} sx={{ px: 0, alignItems: 'flex-start' }}>
                      <ListItemDecorator sx={{ mt: 0.25 }}>
                        <Check sx={{ color: 'success.500', fontSize: 18 }} />
                      </ListItemDecorator>
                      <Typography level="body-sm">
                        {feature}
                      </Typography>
                    </ListItem>
                  ))}
                </List>

                {/* CTA Button */}
                <Button
                  size="lg"
                  fullWidth
                  variant={isCurrent ? 'outlined' : isPopular ? 'solid' : 'soft'}
                  color={isCurrent ? 'neutral' : 'primary'}
                  disabled={isCurrent || isMutating}
                  loading={isMutating}
                  onClick={() => handleSelectTier(tier)}
                  sx={{ mt: 3, fontWeight: 600 }}
                >
                  {getButtonText(tier)}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Footer Note */}
      <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
        <Typography level="body-sm" color="neutral">
          All plans include unlimited team members • Cancel anytime • Secure payments via Stripe
        </Typography>
      </Box>
    </Box>
  );
}
